import type { SpeakerNamesMetadata } from '@/services/api/api.types'

export interface TranscriptSegment {
  id: string
  timeSec: number | null
  label: string
  speakerNumber: number
  name: string | null
  text: string
}

// Speaker marker in link form `[Name](spk_0):` or raw `spk_0:` (optional `[00:00:14]`
// timestamp prefix, as the transcribe processor writes it; `[00:14]` and escaped
// brackets are accepted too). Identity is the `spk_N` target, not the display name.
// 1=hr 2=min 3=sec 4=name(link) 5=spk(link) 6=spk(raw)
const SEGMENT_RE = /(?:\\?\[(?:(\d{1,2}):)?(\d{1,3}):(\d{2})\\?\]\s*)?(?:\[([^\]]*)\]\((spk_\d+)\)|(spk_\d+))\s*:\s*/g

// Matches `spk_0:` and the applied `[Name](spk_0):` link form. True for
// AWS-Transcribe output (uploaded video/audio), not plain YouTube captions.
const SPEAKER_LABEL_RE = /(?:spk_\d+|\[[^\]]*\]\(spk_\d+\))\s*:/m

export function transcriptHasSpeakerLabels(content: string | undefined | null): boolean {
  return !!content && SPEAKER_LABEL_RE.test(content)
}

export function speakerNumberFromLabel(label: string): number {
  const parsed = parseInt(label.replace('spk_', ''), 10)
  return Number.isNaN(parsed) ? 0 : parsed
}

function cleanSegmentText(raw: string): string {
  return raw
    .replace(/\\(?=\s|$)/g, '') // drop line-continuation backslashes
    .replace(/\s+/g, ' ')
    .trim()
}

function toSeconds(hr?: string, min?: string, sec?: string): number | null {
  if (min == null || sec == null) return null
  return parseInt(hr ?? '0', 10) * 3600 + parseInt(min, 10) * 60 + parseInt(sec, 10)
}

/** Split a speaker-labeled transcript into ordered segments keyed by `spk_N`. */
export function parseTranscriptSegments(raw: string): TranscriptSegment[] {
  const markers: {
    index: number
    end: number
    label: string
    name: string | null
    timeSec: number | null
  }[] = []
  SEGMENT_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = SEGMENT_RE.exec(raw)) !== null) {
    const label = m[5] ?? m[6]
    if (!label) continue
    const name = m[4]?.trim() || null
    markers.push({ index: m.index, end: SEGMENT_RE.lastIndex, label, name, timeSec: toSeconds(m[1], m[2], m[3]) })
  }
  return markers.map((mk, i) => {
    const textEnd = i + 1 < markers.length ? markers[i + 1].index : raw.length
    return {
      id: `seg-${i}`,
      label: mk.label,
      speakerNumber: speakerNumberFromLabel(mk.label),
      name: mk.name,
      timeSec: mk.timeSec,
      text: cleanSegmentText(raw.slice(mk.end, textEnd)),
    }
  })
}

export interface TranscriptSpeaker {
  label: string
  speakerNumber: number
  name: string | null
}

/**
 * Speakers in order of appearance (metadata-only labels appended), each resolved to
 * the name the rest of the UI shows for it. Names detected during processing already
 * live in the transcript text, so text wins over metadata to stay in sync with any
 * manual transcript edit. A bare `suggested` never resolves to a name — an AI guess
 * is not an identity until the expert accepts it.
 */
export function collectSpeakers(
  content: string | undefined | null,
  metadata?: SpeakerNamesMetadata | null,
): TranscriptSpeaker[] {
  const order: string[] = []
  const namesFromText: Record<string, string> = {}
  for (const seg of parseTranscriptSegments(content ?? '')) {
    if (!order.includes(seg.label)) order.push(seg.label)
    if (seg.name && !namesFromText[seg.label]) namesFromText[seg.label] = seg.name
  }
  if (metadata) for (const label of Object.keys(metadata)) if (!order.includes(label)) order.push(label)

  return order.map(label => {
    const entry = metadata?.[label]
    return {
      label,
      speakerNumber: speakerNumberFromLabel(label),
      name: namesFromText[label]?.trim() || entry?.accepted?.trim() || null,
    }
  })
}

export function formatTime(totalSec: number | null): string {
  if (totalSec == null) return '--:--'
  const hours = Math.floor(totalSec / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = Math.floor(totalSec % 60)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}
